"use client";

/**
 * FileImport — drag-drop zone. The selected-file list is rendered separately
 * by FileList so it can span full-width below this two-column drop area.
 */

import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@speakai/ui/button";
import { Combobox } from "@/components/shared/combobox";
import { cn } from "@/lib/utils/cn";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";
import { Upload, X, CircleAlert, CloudOff } from "lucide-react";
import {
  ACCEPT_STRING,
  ACCEPT_MIME_TYPES,
  FORMAT_HINT,
} from "../supported-formats";

const ACCEPT_SET = new Set<string>(ACCEPT_MIME_TYPES);

interface FileImportProps {
  onFilesSelected: (files: FileList | File[]) => void;
  errorMessage?: string;
  onClearError?: () => void;
  disabled?: boolean;
  className?: string;
  recordSlot?: React.ReactNode;
  onRejected?: (count: number) => void;
  enableCheckoutTracking?: boolean;
  isOnline?: boolean;
}

export function FileImport({
  onFilesSelected,
  errorMessage,
  onClearError,
  disabled = false,
  className,
  recordSlot,
  onRejected,
  enableCheckoutTracking = false,
  isOnline = true,
}: FileImportProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const hasTrackedFirstAdd = useRef(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled) return;

      const allFiles = Array.from(e.dataTransfer.files);
      const accepted = allFiles.filter((f) => ACCEPT_SET.has(f.type));
      const rejected = allFiles.filter((f) => !ACCEPT_SET.has(f.type));

      if (rejected.length > 0) {
        if (enableCheckoutTracking) {
          pushDataLayer("Upload", "File Rejected", "type", rejected.length);
        }
        onRejected?.(rejected.length);
      }
      if (accepted.length > 0) {
        if (enableCheckoutTracking && !hasTrackedFirstAdd.current) {
          hasTrackedFirstAdd.current = true;
          trackFunnel("upload", "file_added", { file_type: accepted[0].type, file_count: accepted.length });
          pushDataLayer("Upload", "Files Selected", "", accepted.length, { file_type: accepted[0].type });
        }
        onFilesSelected(accepted);
      }
    },
    [disabled, enableCheckoutTracking, onFilesSelected, onRejected]
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    },
    []
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = e.target.files;
      if (selectedFiles && selectedFiles.length > 0) {
        if (enableCheckoutTracking && !hasTrackedFirstAdd.current) {
          hasTrackedFirstAdd.current = true;
          trackFunnel("upload", "file_added", { file_type: selectedFiles[0].type, file_count: selectedFiles.length });
          pushDataLayer("Upload", "Files Selected", "", selectedFiles.length, { file_type: selectedFiles[0].type });
        }
        onFilesSelected(selectedFiles);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [enableCheckoutTracking, onFilesSelected]
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className={cn("flex flex-col gap-4 h-full", className)}>
      {recordSlot && (
        <div className="block sm:hidden text-center text-xs text-muted-foreground">
          {recordSlot}
        </div>
      )}

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={t("UPLOAD_FILE.DROP_ZONE_LABEL", "Browse for files to upload, or drop files here")}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
        onClick={handleBrowseClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleBrowseClick();
          }
        }}
        data-testid="file-drop-zone"
        data-dragging={isDragging}
        className={cn(
          "flex flex-1 cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-10 text-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[180px]",
          disabled
            ? "cursor-not-allowed border-border dark:border-border/40 bg-muted/50"
            : "border-border dark:border-border/60 bg-muted/30 hover:border-primary/50 hover:bg-primary/5",
          isDragging && "border-primary bg-primary/10"
        )}
      >
        <Upload className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
        <div className="text-center">
          <p className="text-sm font-medium text-foreground">
            {t("UPLOAD_FILE.DESC", "Upload or Drop your file here")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {FORMAT_HINT}
          </p>
        </div>
        {recordSlot && (
          <div
            className="hidden sm:block mt-3 border-t border-border pt-3 text-xs text-muted-foreground w-full text-center"
            onClick={(e) => e.stopPropagation()}
          >
            {recordSlot}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={ACCEPT_STRING}
        onChange={handleFileInputChange}
        className="hidden"
        disabled={disabled}
        data-testid="file-input"
      />

      {errorMessage && (
        <div className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/5 p-3" data-testid="file-import-error">
          <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-danger" aria-hidden="true" />
          <div className="flex-1">
            <p className="text-sm text-danger whitespace-pre-line">
              {errorMessage}
            </p>
          </div>
          {onClearError && (
            <button
              type="button"
              onClick={onClearError}
              aria-label={t("ACTION.CLEAR_ERROR", "Clear error")}
              className="flex-shrink-0 text-danger/60 hover:text-danger focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      {!isOnline && (
        <div role="status" aria-live="polite" className="flex items-center gap-2 rounded-md border border-warning/50 bg-warning/10 p-3 text-sm text-warning">
          <CloudOff aria-hidden="true" className="h-4 w-4 flex-shrink-0" />
          <span>{t("UPLOAD.OFFLINE_BANNER", "You're offline. Uploads will resume when your connection returns.")}</span>
        </div>
      )}

    </div>
  );
}
